import { FC } from 'react';
import styled from 'styled-components';

import { urlRegex } from '~src/constants';
import { media } from '~src/styles/media-templates';

const TextWithLink = styled.span<{ $noStyle?: boolean }>`
  word-break: break-word;
  white-space: pre-wrap;
  overflow: auto;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  ${media.phone`
    -webkit-line-clamp: 13;
  `}
  > a {
    color: ${({ theme, $noStyle }) => ($noStyle ? 'inherit' : theme.baseBlue.a10)};
    text-decoration: underline;
    &:hover {
      opacity: 0.7;
    }
  }
`;

const firstPattern = /(\[[A-Za-z0-9\s]+\|)/;
const endPattern = /\]/;
// tslint:disable-next-line:max-line-length
const plainUrlRegex = new RegExp(urlRegex.source, 'g');
// BE CAREFUL WITH GLOBAL FLAG: https://stackoverflow.com/questions/1520800/why-does-a-regexp-with-global-flag-give-wrong-results
// tslint:disable-next-line:max-line-length
const urlPatternRegex = new RegExp(`(${firstPattern.source}${urlRegex.source}${endPattern.source})`);
// tslint:disable-next-line:max-line-length
const regexWithProtocol = /(http|https|ftp|ftps):\/\/[a-zA-Z0-9\-.]+\.[a-zA-Z]{2,3}(\/\S*)?/;
// tslint:disable-next-line:max-line-length
const plainOrPatternLink = new RegExp(
  `(${firstPattern.source}*${urlRegex.source}${endPattern.source}?)`,
  'g',
);
// tslint:disable-next-line:max-line-length
const topLevelUrl =
  /(([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)/g;

function getProtocoledLink(url: string, placeholder: string, blocked?: boolean) {
  if (blocked) {
    return placeholder;
  }
  let tmpUrl = url;
  if (!regexWithProtocol.test(url)) {
    tmpUrl = `//${url}`;
  }
  return `<a target="_blank"  href="${tmpUrl}">${placeholder}</a>`;
}

const replaceLinkOrPattern = (textSnippet: string, blocked?: boolean) => {
  // get every string if plain url or with pattern
  let url = '';
  let placeholder = '';
  // check if the string is in the pattern [<text>|<link>] format
  const isPattern = urlPatternRegex.test(textSnippet);

  if (isPattern) {
    // if its in the pattern format split the placeholder and url
    const singleStringSplit = textSnippet.split('|');
    placeholder = singleStringSplit[0].replace('[', '');
    url = singleStringSplit[1].replace(']', '');
  } else {
    const plainUrlCollection = textSnippet.match(plainUrlRegex);

    if (plainUrlCollection && plainUrlCollection.length > 0) {
      url = plainUrlCollection[0];
      placeholder = url;

      // if not in pattern format check if url is more than 40 characters...
      if (url.length > 40) {
        const topLevel = url.match(topLevelUrl);
        // ... and replace it after subdomain with "..."
        if (topLevel) {
          placeholder = `${topLevel[0]}...`;
        }
      }
      // replace the strings with or without additional protocol
      return textSnippet.replace(plainUrlRegex, () => getProtocoledLink(url, placeholder, blocked));
    }
  }
  return getProtocoledLink(url, placeholder, blocked);
};

const replaceCleanUrlify = (text: string, blocked?: boolean): string => {
  const replace = (txt: string) => {
    return replaceLinkOrPattern(txt, blocked);
  };
  if (plainOrPatternLink.test(text)) {
    return text.replace(plainOrPatternLink, replace);
  }
  return text;
};

interface Props {
  text: string;
  noStyle?: boolean;
  blocked?: boolean;
}

const UrlText: FC<Props> = ({ text, noStyle, blocked }) => {
  const urlifiedText = replaceCleanUrlify(text, blocked);
  return <TextWithLink $noStyle={noStyle} dangerouslySetInnerHTML={{ __html: urlifiedText }} />;
};

export default UrlText;
